import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { ToastService } from '../../../core/services/toast.service';
import { S3ImageUploadService } from '../../../core/services/s3-image-upload.service';
import { API_ROUTES } from '../../../core/constants/api-routes';

// ── Types ──────────────────────────────────────────────────────────────────────

type SettingsTab = 'profile' | 'security' | 'notifications' | 'subscription';

/** Numeric enum matching API values for story privacy (visibilityTypeList, typeId=6). */
export enum PrivacyLevel {
  Private         = 1,
  Shared          = 2,
  Public          = 3,
  Family          = 4,
  SelectedMembers = 5,
}

/** Numeric enum matching API values for profile visibility (profileVisibilityTypeList, typeId=11). */
export enum ProfileVisibilityLevel {
  OnlyMe = 1,
  Family = 2,
  Public = 3,
}

/** A single item from GetAllMasterList response lists. */
interface MasterListItem {
  id:   number;
  name: string;
}

/** Shape of POST /Common/GetAllMasterList → data */
interface MasterListData {
  visibilityTypeList:        MasterListItem[] | null;
  profileVisibilityTypeList: MasterListItem[] | null;
}

/** Shape of GET /UserSettings/GetProfileSettings → data */
interface ProfileSettingsData {
  userId:           string;
  firstName:        string;
  lastName:         string;
  profilePhotoUrl:  string;
  email:            string;
  phoneNumber:      string;
  familyName:       string;
  familyPhotoUrl:   string;
  isSocialLogin:    boolean;
  providerType:     number;
  providerTypeName: number;
}

/** Shape of GET /UserSettings/GetPrivacySettings → data */
interface PrivacySettingsData {
  defaultStoryPrivacy:     number;
  defaultStoryPrivacyName: string;
  profileVisibility:       number;
  profileVisibilityName:   string;
  isSocialLogin:           boolean;
}

/** Shape of GET /UserSettings/GetNotificationSettings → data */
interface NotificationSettingsData {
  notifyNewMemberAdditions: boolean;
  notifyScheduledPrompts:   boolean;
}

/** A family group that requires ownership transfer before account deletion. */
interface OwnedFamilyGroup {
  familySpaceId:              string;
  familyName:                 string;
  memberCount:                number;
  requiresOwnershipTransfer:  boolean;
}

/** Shape of POST /UserSettings/PreCheckAccountDeletion → data */
interface PreCheckDeletionData {
  canDelete:          boolean;
  ownedFamilyGroups:  OwnedFamilyGroup[];
  message:            string;
}

/** Allowed MIME types for image uploads. */
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Max profile / family photo size: 5 MB. */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// ── Validators ─────────────────────────────────────────────────────────────────

const passwordMatchValidator: ValidatorFn = (control: AbstractControl) => {
  const newPassword = control.get('newPassword');
  const confirmPassword = control.get('confirmPassword');
  if (!newPassword || !confirmPassword) return null;
  return newPassword.value === confirmPassword.value ? null : { passwordMismatch: true };
};

@Component({
  selector: 'app-settings',
  templateUrl: './settings.html',
  styleUrl: './settings.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule],
})
export class Settings implements OnInit {

  // ── Dependency Injection ───────────────────────────────────────────────────
  private readonly fb          = inject(FormBuilder);
  private readonly api         = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly toast       = inject(ToastService);
  private readonly s3          = inject(S3ImageUploadService);
  private readonly router      = inject(Router);

  // ── Template refs ─────────────────────────────────────────────────────────
  @ViewChild('imageInput')       imageInput!:       ElementRef<HTMLInputElement>;
  @ViewChild('familyImageInput') familyImageInput!: ElementRef<HTMLInputElement>;

  // ── Tab state ─────────────────────────────────────────────────────────────
  readonly activeTab = signal<SettingsTab>('profile');

  // ── Profile state ─────────────────────────────────────────────────────────
  readonly isLoadingProfile         = signal(false);
  readonly isSavingProfile          = signal(false);
  /** True while the profile photo is being uploaded to S3 + API. */
  readonly isUploadingProfileImage  = signal(false);
  /** True while the profile photo is being removed via API. */
  readonly isRemovingProfileImage   = signal(false);
  /** Upload progress percentage (0–100). Drives the circular progress ring. */
  readonly uploadProgress           = signal(0);

  /** URL of the current profile photo (from API or local preview). */
  readonly profileImageUrl = signal<string>('');
  /** URL of the current family photo (from API or local preview). */
  readonly familyImageUrl  = signal<string>('');

  /** Pending File objects — set on selection, sent to API on save. */
  private familyPhotoFile:  File | null = null;

  /** Read-only display values populated from the API (email & phone are not editable). */
  readonly profileEmail = signal<string>('');
  readonly profilePhone = signal<string>('');

  /** Full display name — updated after load and after every successful save. */
  readonly displayName = signal<string>('You');

  /** Two-letter initials for the avatar fallback — updated after load and after every successful save. */
  readonly profileInitials = signal<string>('?');

  /** Recalculates displayName and profileInitials from the current form values. */
  private syncNameSignals(): void {
    const first = (this.profileForm.get('firstName')?.value as string)?.trim() ?? '';
    const last  = (this.profileForm.get('lastName')?.value  as string)?.trim() ?? '';
    this.displayName.set([first, last].filter(Boolean).join(' ') || 'You');
    const f = first[0]?.toUpperCase() ?? '';
    const l = last[0]?.toUpperCase()  ?? '';
    this.profileInitials.set(f + l || '?');
  }

  // ── Privacy state ─────────────────────────────────────────────────────────
  readonly isLoadingPrivacy    = signal(false);
  readonly isSavingStoryPrivacy    = signal(false);
  readonly isSavingProfileVisibility = signal(false);

  /** Numeric value loaded from API (matches PrivacyLevel / ProfileVisibilityLevel enum IDs). */
  readonly defaultStoryPrivacy = signal<number>(0);
  readonly profileVisibility   = signal<number>(0);

  /** Exposed enums to template for comparison. */
  readonly PrivacyLevel = PrivacyLevel;
  readonly ProfileVisibilityLevel = ProfileVisibilityLevel;

  /** Dynamic options fetched from GetAllMasterList (typeId=6). */
  readonly storyPrivacyOptions = signal<MasterListItem[]>([]);

  /** Dynamic options fetched from GetAllMasterList (typeId=11). */
  readonly profileVisibilityOptions = signal<MasterListItem[]>([]);

  // ── Password / Security state ─────────────────────────────────────────────
  readonly showPasswordForm      = signal(false);
  readonly isChangingPassword    = signal(false);
  readonly showCurrentPassword   = signal(false);
  readonly showNewPassword       = signal(false);
  readonly showConfirmPassword   = signal(false);

  // ── Social login flag (set from profile API — hides Change Password for OAuth users) ───
  readonly isSocialLogin = signal(false);

  // ── Notification state ────────────────────────────────────────────────────
  readonly isLoadingNotifications   = signal(false);
  readonly isSavingNotifications    = signal(false);
  readonly notifyNewMemberAdditions = signal(false);
  readonly notifyScheduledPrompts   = signal(false);
  // ── Delete account state ───────────────────────────────────
  readonly showDeleteModal          = signal(false);
  readonly isDeletingAccount        = signal(false);
  readonly isPreCheckingDeletion    = signal(false);
  readonly showOwnershipBlockerModal = signal(false);
  readonly ownedFamilyGroups        = signal<OwnedFamilyGroup[]>([]);
  // ── Forms ─────────────────────────────────────────────────────────────────

  profileForm = this.fb.group({
    firstName:  ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
    lastName:   ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]],
    familyName: ['', [Validators.maxLength(100)]],
  });

  /**
   * Password form — API calls are commented; form validation is wired.
   * Wire up to API_ROUTES.SETTINGS_CHANGE_PASSWORD when ready.
   */
  passwordForm = this.fb.group(
    {
      currentPassword: ['', [Validators.required, Validators.maxLength(100)]],
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.maxLength(20),
          Validators.pattern(/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,20}$/),
        ],
      ],
      confirmPassword: ['', [Validators.required, Validators.maxLength(20)]],
    },
    { validators: passwordMatchValidator }
  );

  // ── Convenience accessors ──────────────────────────────────────────────────

  /** Alias for profileForm.controls — used in the template as `pf['x']`. */
  get pf() { return this.profileForm.controls; }

  /** Alias for passwordForm.controls — used in the template as `pwf['x']`. */
  get pwf() { return this.passwordForm.controls; }

  /** Live password strength criteria — used by the hints panel in the template. */
  get passwordCriteria() {
    const val: string = this.passwordForm.get('newPassword')?.value || '';
    return {
      minLength:  val.length >= 8,
      maxLength:  val.length <= 20 && val.length > 0,
      hasUpper:   /[A-Z]/.test(val),
      hasNumber:  /[0-9]/.test(val),
      hasSpecial: /[@$!%*?&]/.test(val),
    };
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ──────────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadMasterLists();
    this.loadProfileSettings();
    this.loadPrivacySettings();
    this.loadNotificationSettings();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Tab navigation
  // ──────────────────────────────────────────────────────────────────────────

  setTab(tab: SettingsTab): void {
    this.activeTab.set(tab);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Master lists
  // ──────────────────────────────────────────────────────────────────────────

  /** Fetches story-privacy (typeId=6) and profile-visibility (typeId=11) option lists in one call. */
  private async loadMasterLists(): Promise<void> {
    try {
      const res = await this.api.request<MasterListData>(
        'POST',
        API_ROUTES.COMMON_GET_ALL_MASTER_LIST,
        { typeIds: [6, 11] },
        { showToaster: false }
      );
      if (res.success && res.data) {
        this.storyPrivacyOptions.set(res.data.visibilityTypeList        ?? []);
        this.profileVisibilityOptions.set(res.data.profileVisibilityTypeList ?? []);
      }
    } catch {
      // options stay empty — pills won't render; non-fatal
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Profile
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Loads profile settings from GET /UserSettings/GetProfileSettings.
   */
  private async loadProfileSettings(): Promise<void> {
    this.isLoadingProfile.set(true);
    try {
      const res = await this.api.request<ProfileSettingsData>(
        'GET',
        API_ROUTES.SETTINGS_GET_PROFILE,
        undefined,
        { showToaster: false }
      );
      if (res.success && res.data) {
        const d = res.data;
        this.profileForm.patchValue({
          firstName:  d.firstName  ?? '',
          lastName:   d.lastName   ?? '',
          familyName: d.familyName ?? '',
        });
        this.profileEmail.set(d.email       ?? '');
        this.profilePhone.set(d.phoneNumber ?? '');
        this.profileImageUrl.set(d.profilePhotoUrl ?? '');
        this.familyImageUrl.set(d.familyPhotoUrl   ?? '');
        this.isSocialLogin.set(d.isSocialLogin ?? false);
        this.syncNameSignals();
      }
    } finally {
      this.isLoadingProfile.set(false);
    }
  }

  /**
   * Saves updated profile:
   * 1. Uploads new profile photo to S3 member-photos → calls UpdateProfileImage API
   * 2. Uploads new family photo to S3 member-photos
   * 3. Calls UpdateProfileSettings with all text fields + final S3 URLs
   */
  async saveProfile(): Promise<void> {
    this.profileForm.markAllAsTouched();
    if (this.profileForm.invalid || this.isSavingProfile()) return;

    // Guard: S3 must be configured before we can upload family photo
    if (this.familyPhotoFile && !this.s3.isConfigured()) {
      this.toast.error('Image upload is not configured. Please contact support.');
      return;
    }

    this.isSavingProfile.set(true);
    try {
      // ── Step 1: upload family photo to S3 if changed ───────────────────
      if (this.familyPhotoFile) {
        let s3Result;
        try {
          s3Result = await this.s3.upload(this.familyPhotoFile, 'memberPhoto');
        } catch {
          this.toast.error('Family photo upload failed. Please try again.');
          return;
        }
        this.familyImageUrl.set(s3Result.url);
        this.familyPhotoFile = null;
      }

      // ── Step 3: save all profile text fields + final image URLs ────────
      const payload = {
        firstName:       this.profileForm.value.firstName  ?? '',
        lastName:        this.profileForm.value.lastName   ?? '',
        profilePhotoUrl: this.profileImageUrl(),
        familyName:      this.profileForm.value.familyName ?? '',
        familyPhotoUrl:  this.familyImageUrl(),
      };
      const res = await this.api.request<boolean>(
        'POST',
        API_ROUTES.SETTINGS_UPDATE_PROFILE,
        payload
      );
      if (res.success) {
        this.syncNameSignals();
      }
    } finally {
      this.isSavingProfile.set(false);
    }
  }

  /** Opens the hidden profile photo file picker. */
  triggerImageUpload(): void {
    this.imageInput?.nativeElement.click();
  }

  /** Opens the hidden family photo file picker. */
  triggerFamilyImageUpload(): void {
    this.familyImageInput?.nativeElement.click();
  }

  /**
   * Validates a selected image: type must be JPG / PNG / WEBP, max 5 MB.
   * Returns an error message string on failure, null on success.
   */
  private validateImageFile(file: File): string | null {
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      return 'Only JPG, PNG, and WEBP images are allowed.';
    }
    if (file.size > MAX_IMAGE_BYTES) {
      return 'Image must be smaller than 5 MB.';
    }
    return null;
  }

  /** Handles profile photo selection — immediately uploads to S3 and calls UpdateProfileImage. */
  async onImageSelected(event: Event): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    (event.target as HTMLInputElement).value = '';
    if (!file) return;
    const err = this.validateImageFile(file);
    if (err) { this.toast.error(err); return; }

    if (!this.s3.isConfigured()) {
      this.toast.error('Image upload is not configured. Please contact support.');
      return;
    }

    // Show local preview immediately
    this.profileImageUrl.set(URL.createObjectURL(file));
    this.isUploadingProfileImage.set(true);
    this.uploadProgress.set(0);

    // Simulate progress: 0→40 quickly (S3 upload), then 40→80 (API call), then 80→100 (done)
    const progressInterval = setInterval(() => {
      const current = this.uploadProgress();
      if (current < 40) {
        this.uploadProgress.set(Math.min(current + 8, 40));
      }
    }, 150);

    try {
      const s3Result = await this.s3.upload(file, 'memberPhoto');
      clearInterval(progressInterval);
      this.uploadProgress.set(60);

      // Replace blob preview with the real S3 URL
      this.profileImageUrl.set(s3Result.url);

      this.uploadProgress.set(80);
      await this.api.request<boolean>(
        'POST',
        API_ROUTES.SETTINGS_UPDATE_PROFILE_IMAGE,
        { imageName: s3Result.fileName },
        { showLoader: false }
      );
      this.uploadProgress.set(100);

      // Brief delay so user sees 100% before hiding
      await new Promise(r => setTimeout(r, 400));
    } catch {
      clearInterval(progressInterval);
      // Revert preview on failure
      this.profileImageUrl.set('');
      this.toast.error('Profile photo upload failed. Please try again.');
    } finally {
      this.isUploadingProfileImage.set(false);
      this.uploadProgress.set(0);
    }
  }

  /** Removes the profile photo by sending imageName: null to the API. */
  async removeProfileImage(): Promise<void> {
    if (this.isUploadingProfileImage() || this.isRemovingProfileImage()) return;
    this.isRemovingProfileImage.set(true);
    try {
      await this.api.request<boolean>(
        'POST',
        API_ROUTES.SETTINGS_UPDATE_PROFILE_IMAGE,
        { imageName: null },
        { showLoader: false }
      );
      this.profileImageUrl.set('');
    } finally {
      this.isRemovingProfileImage.set(false);
    }
  }

  /** Handles family photo selection — previews locally, stores file for save. */
  onFamilyImageSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    (event.target as HTMLInputElement).value = '';
    if (!file) return;
    const err = this.validateImageFile(file);
    if (err) { this.toast.error(err); return; }
    this.familyPhotoFile = file;
    this.familyImageUrl.set(URL.createObjectURL(file));
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Privacy
  // ──────────────────────────────────────────────────────────────────────────

  /** Loads privacy settings from GET /UserSettings/GetPrivacySettings. */
  private async loadPrivacySettings(): Promise<void> {
    this.isLoadingPrivacy.set(true);
    try {
      const res = await this.api.request<PrivacySettingsData>(
        'GET',
        API_ROUTES.SETTINGS_GET_PRIVACY,
        undefined,
        { showToaster: false }
      );
      if (res.success && res.data) {
        this.defaultStoryPrivacy.set(res.data.defaultStoryPrivacy ?? 0);
        this.profileVisibility.set(res.data.profileVisibility     ?? 0);
      }
    } finally {
      this.isLoadingPrivacy.set(false);
    }
  }

  /**
   * Immediately saves the selected Default Story Privacy via
   * POST /UserSettings/UpdateDefaultStoryPrivacy.
   */
  async setDefaultStoryPrivacy(value: number): Promise<void> {
    if (this.isSavingStoryPrivacy() || this.defaultStoryPrivacy() === value) return;
    this.defaultStoryPrivacy.set(value);
    this.isSavingStoryPrivacy.set(true);
    try {
      const res = await this.api.request<boolean>(
        'POST',
        API_ROUTES.SETTINGS_UPDATE_DEFAULT_STORY_PRIVACY,
        { defaultStoryPrivacy: value }
      );
      if (!res.status) {
        // Revert optimistic update on failure
        await this.loadPrivacySettings();
      }
    } finally {
      this.isSavingStoryPrivacy.set(false);
    }
  }

  /**
   * Immediately saves Profile Visibility via
   * POST /UserSettings/UpdateProfileVisibility.
   * Takes effect immediately as per business rules.
   */
  async setProfileVisibility(value: number): Promise<void> {
    if (this.isSavingProfileVisibility() || this.profileVisibility() === value) return;
    this.profileVisibility.set(value);
    this.isSavingProfileVisibility.set(true);
    try {
      const res = await this.api.request<boolean>(
        'POST',
        API_ROUTES.SETTINGS_UPDATE_PROFILE_VISIBILITY,
        { profileVisibility: value }
      );
      if (!res.status) {
        // Revert optimistic update on failure
        await this.loadPrivacySettings();
      }
    } finally {
      this.isSavingProfileVisibility.set(false);
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Password / Security
  // ──────────────────────────────────────────────────────────────────────────

  togglePasswordForm(): void {
    this.showPasswordForm.update(v => !v);
    if (!this.showPasswordForm()) {
      this.passwordForm.reset();
    }
  }

  cancelPasswordForm(): void {
    this.showPasswordForm.set(false);
    this.passwordForm.reset();
  }

  /**
   * Submits the change-password form.
   * POST /UserSettings/ChangePassword — not available for social login users.
   */
  async changePassword(): Promise<void> {
    this.passwordForm.markAllAsTouched();
    if (this.passwordForm.invalid || this.isChangingPassword()) return;

    this.isChangingPassword.set(true);
    try {
      const { currentPassword, newPassword, confirmPassword } = this.passwordForm.value;
      const res = await this.api.request<boolean>(
        'POST',
        API_ROUTES.SETTINGS_CHANGE_PASSWORD,
        {
          currentPassword,
          newPassword,
          confirmNewPassword: confirmPassword,
        }
      );
      if (res.status) {
        this.cancelPasswordForm();
      }
    } finally {
      this.isChangingPassword.set(false);
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Notifications
  // ──────────────────────────────────────────────────────────────────────────

  /** Loads notification settings from GET /UserSettings/GetNotificationSettings. */
  private async loadNotificationSettings(): Promise<void> {
    this.isLoadingNotifications.set(true);
    try {
      const res = await this.api.request<NotificationSettingsData>(
        'GET',
        API_ROUTES.SETTINGS_GET_NOTIFICATIONS,
        undefined,
        { showToaster: false }
      );
      if (res.success && res.data) {
        this.notifyNewMemberAdditions.set(res.data.notifyNewMemberAdditions ?? false);
        this.notifyScheduledPrompts.set(res.data.notifyScheduledPrompts   ?? false);
      }
    } finally {
      this.isLoadingNotifications.set(false);
    }
  }

  /** Saves notification preferences via PUT /UserSettings/UpdateNotificationSettings. */
  async saveNotifications(): Promise<void> {
    if (this.isSavingNotifications()) return;

    this.isSavingNotifications.set(true);
    try {
      const res = await this.api.request<boolean>(
        'POST',
        API_ROUTES.SETTINGS_UPDATE_NOTIFICATIONS,
        {
          notifyNewMemberAdditions: this.notifyNewMemberAdditions(),
          notifyScheduledPrompts:   this.notifyScheduledPrompts(),
        }
      );
      // Backend message shown automatically by ApiService (showToaster: true)
    } finally {
      this.isSavingNotifications.set(false);
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Delete Account
  // ──────────────────────────────────────────────────────────────────────────

  /** Runs the pre-check, then either opens the confirm modal or the ownership-blocker modal. */
  async openDeleteAccountModal(): Promise<void> {
    if (this.isPreCheckingDeletion()) return;
    this.isPreCheckingDeletion.set(true);
    try {
      const res = await this.api.request<PreCheckDeletionData>(
        'GET',
        API_ROUTES.SETTINGS_PRE_CHECK_DELETION,
        undefined,
        { showToaster: false }
      );
      if (!res.success || !res.data) {
        this.toast.error(res.message || 'Unable to verify account status. Please try again.');
        return;
      }
      const groups = (res.data.ownedFamilyGroups ?? []).filter(g => g.requiresOwnershipTransfer);
      if (!res.data.canDelete || groups.length > 0) {
        this.ownedFamilyGroups.set(groups);
        this.showOwnershipBlockerModal.set(true);
      } else {
        this.showDeleteModal.set(true);
      }
    } finally {
      this.isPreCheckingDeletion.set(false);
    }
  }

  closeOwnershipBlockerModal(): void {
    this.showOwnershipBlockerModal.set(false);
    this.ownedFamilyGroups.set([]);
  }

  closeDeleteModal(): void {
    if (this.isDeletingAccount()) return;
    this.showDeleteModal.set(false);
  }

  /** Submits the final account deletion request. */
  async confirmDeleteAccount(): Promise<void> {
    if (this.isDeletingAccount()) return;
    this.isDeletingAccount.set(true);
    try {
      const res = await this.api.request<boolean>(
        'POST',
        API_ROUTES.SETTINGS_DELETE_ACCOUNT,
        { confirmDeletion: true, reason: '' }
      );
      if (res.success) {
        this.showDeleteModal.set(false);
        this.authService.logout();
        this.router.navigate(['/login']);
      }
    } finally {
      this.isDeletingAccount.set(false);
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Auth
  // ──────────────────────────────────────────────────────────────────────────

  logout(): void {
    this.authService.logout();
    this.router.navigate(['/login']);
  }
}

